/**
 * FE-14~19: Admin Metrics API layer.
 * Maps to: GET /admin/metrics/{growth,funnel,revenue,system,behavior,feature-value}
 *
 * The backend returns flat metric rows (metric_date, metric_name, metric_value).
 * This layer pivots them into the column-per-metric shape the UI expects.
 */

import { apiFetch } from "./client";

// ─── Display types (consumed by UI components) ───

export type GrowthDayRow = {
  readonly date: string;
  readonly dau: number;
  readonly new_users: number;
  readonly messages: number;
  readonly summary_requests: number;
};

export type GrowthMetrics = {
  readonly rows: ReadonlyArray<GrowthDayRow>;
  readonly totals: {
    readonly dau_avg: number;
    readonly new_users: number;
    readonly messages: number;
    readonly summary_requests: number;
  };
};

export type FunnelStep = {
  readonly name: string;
  readonly users: number;
  readonly rate: number;
};

export type FunnelResult = {
  readonly funnel_name: string;
  readonly steps: ReadonlyArray<FunnelStep>;
};

export type RevenueDayRow = {
  readonly date: string;
  readonly mrr: number;
  readonly paid_users: number;
  readonly checkout_count: number;
  readonly topup_count: number;
};

export type RevenueMetrics = {
  readonly rows: ReadonlyArray<RevenueDayRow>;
  readonly totals: {
    readonly mrr: number;
    readonly paid_users: number;
    readonly checkout_count: number;
    readonly topup_count: number;
  };
};

export type SystemDayRow = {
  readonly date: string;
  readonly summary_requests: number;
  readonly summary_success: number;
  readonly summary_failed: number;
  readonly storage_bytes: number;
  readonly webhook_failures: number;
};

export type SystemMetrics = {
  readonly rows: ReadonlyArray<SystemDayRow>;
  readonly totals: {
    readonly summary_requests: number;
    readonly success_rate: number;
    readonly storage_bytes: number;
    readonly webhook_failures: number;
  };
};

export type BehaviorDayRow = {
  readonly date: string;
  readonly sessions_per_user: number;
  readonly messages_per_user: number;
  readonly summaries_per_user: number;
  readonly bookmarks_per_user: number;
  readonly shares_per_user: number;
  readonly agent_usage_rate: number;
  readonly retention_rate: number;
};

export type BehaviorMetrics = {
  readonly rows: ReadonlyArray<BehaviorDayRow>;
  readonly totals: {
    readonly sessions_per_user: number;
    readonly messages_per_user: number;
    readonly summaries_per_user: number;
    readonly bookmarks_per_user: number;
    readonly shares_per_user: number;
    readonly agent_usage_rate: number;
    readonly retention_rate: number;
  };
  readonly message_breakdown: {
    readonly human: number;
    readonly agent: number;
    readonly group: number;
  };
};

export type FeatureValueRow = {
  readonly feature_name: string;
  readonly exposure: number;
  readonly adoption: number;
  readonly repeat: number;
  readonly retention_lift: number;
};

export type FeatureValueMetrics = {
  readonly rows: ReadonlyArray<FeatureValueRow>;
};

export type MessageAnalytics = {
  readonly daily_trends: ReadonlyArray<{
    date: string;
    human: number;
    agent: number;
    system: number;
  }>;
  readonly type_distribution: ReadonlyArray<{
    content_type: string;
    count: number;
    percentage: number;
  }>;
  readonly recall_trends: ReadonlyArray<{
    date: string;
    total: number;
    recalled: number;
    rate: number;
  }>;
  readonly active_conversations: ReadonlyArray<{
    date: string;
    count: number;
  }>;
  readonly totals: {
    messages: number;
    human_ratio: number;
    agent_ratio: number;
    recall_rate: number;
  };
};

export type DateRangeParams = {
  readonly start_date: string;
  readonly end_date: string;
};

// ─── Raw backend response shapes ───

type MetricRow = {
  readonly metric_date: string;
  readonly metric_name: string;
  readonly metric_value: number;
};

type BackendFunnelStep = {
  readonly step_name: string;
  readonly step_order: number;
  readonly user_count: number;
};

type BackendFunnelData = {
  readonly funnel_name: string;
  readonly steps: ReadonlyArray<BackendFunnelStep>;
};

type GrowthResponse = {
  readonly items: ReadonlyArray<MetricRow>;
  readonly total: number;
};

type FunnelResponse = {
  readonly funnels: ReadonlyArray<BackendFunnelData>;
};

type RevenueResponse = {
  readonly items: ReadonlyArray<MetricRow>;
};

type SystemResponse = {
  readonly items: ReadonlyArray<MetricRow>;
};

type BehaviorResponse = {
  readonly behavior: ReadonlyArray<MetricRow>;
  readonly messages: ReadonlyArray<MetricRow>;
};

type BackendFeatureRow = {
  readonly metric_date: string;
  readonly feature_name: string;
  readonly exposure_count: number;
  readonly adoption_count: number;
  readonly repeat_count: number;
  readonly retention_lift: number | null;
  readonly platform: string | null;
  readonly country: string | null;
};

type FeatureValueResponse = {
  readonly items: ReadonlyArray<BackendFeatureRow>;
};

// ─── API Calls ───

export async function fetchGrowthMetrics(
  params: DateRangeParams,
): Promise<GrowthMetrics> {
  const qs = toQueryString(params);
  const res = await apiFetch<{ data: GrowthResponse }>(
    `/admin/metrics/growth?${qs}`,
  );
  return toGrowthMetrics(res.data);
}

export async function fetchFunnelMetrics(
  params: DateRangeParams & { readonly funnel: string },
): Promise<FunnelResult> {
  const qs = toQueryString(params);
  const res = await apiFetch<{ data: FunnelResponse }>(
    `/admin/metrics/funnel?${qs}`,
  );
  const raw = res.data.funnels[0];
  if (!raw) return { funnel_name: params.funnel, steps: [] };
  return toFunnelResult(raw);
}

export async function fetchRevenueMetrics(
  params: DateRangeParams,
): Promise<RevenueMetrics> {
  const qs = toQueryString(params);
  const res = await apiFetch<{ data: RevenueResponse }>(
    `/admin/metrics/revenue?${qs}`,
  );
  return toRevenueMetrics(res.data.items);
}

export async function fetchSystemMetrics(
  params: DateRangeParams,
): Promise<SystemMetrics> {
  const qs = toQueryString(params);
  const res = await apiFetch<{ data: SystemResponse }>(
    `/admin/metrics/system?${qs}`,
  );
  return toSystemMetrics(res.data.items);
}

export async function fetchBehaviorMetrics(
  params: DateRangeParams,
): Promise<BehaviorMetrics> {
  const qs = toQueryString(params);
  const res = await apiFetch<{ data: BehaviorResponse }>(
    `/admin/metrics/behavior?${qs}`,
  );
  return toBehaviorMetrics(res.data);
}

export async function fetchMessageAnalytics(
  params: DateRangeParams,
): Promise<MessageAnalytics> {
  const qs = toQueryString(params);
  const res = await apiFetch<{ data: MessageAnalytics }>(
    `/admin/metrics/messages?${qs}`,
  );
  return res.data;
}

export async function fetchFeatureValueMetrics(
  params: DateRangeParams,
): Promise<FeatureValueMetrics> {
  const qs = toQueryString(params);
  const res = await apiFetch<{ data: FeatureValueResponse }>(
    `/admin/metrics/feature-value?${qs}`,
  );
  return toFeatureValueMetrics(res.data);
}

// ─── Pivot Transformers ───

function toGrowthMetrics(raw: GrowthResponse): GrowthMetrics {
  const byDate = new Map<string, Mutable<GrowthDayRow>>();

  for (const r of raw.items) {
    let entry = byDate.get(r.metric_date);
    if (!entry) {
      entry = {
        date: r.metric_date,
        dau: 0,
        new_users: 0,
        messages: 0,
        summary_requests: 0,
      };
      byDate.set(r.metric_date, entry);
    }
    assignMetric(entry, r.metric_name, r.metric_value, GROWTH_FIELDS);
  }

  const rows = sortByDate(byDate);
  return {
    rows,
    totals: {
      dau_avg: avg(rows, "dau"),
      new_users: sum(rows, "new_users"),
      messages: sum(rows, "messages"),
      summary_requests: sum(rows, "summary_requests"),
    },
  };
}

function toFunnelResult(raw: BackendFunnelData): FunnelResult {
  const steps: FunnelStep[] = [];
  const sorted = [...raw.steps].sort((a, b) => a.step_order - b.step_order);
  const firstUsers = sorted[0]?.user_count ?? 0;

  for (const s of sorted) {
    steps.push({
      name: s.step_name,
      users: s.user_count,
      rate: firstUsers > 0 ? (s.user_count / firstUsers) * 100 : 0,
    });
  }

  return { funnel_name: raw.funnel_name, steps };
}

function toRevenueMetrics(items: ReadonlyArray<MetricRow>): RevenueMetrics {
  const byDate = new Map<string, Mutable<RevenueDayRow>>();

  for (const r of items) {
    let entry = byDate.get(r.metric_date);
    if (!entry) {
      entry = {
        date: r.metric_date,
        mrr: 0,
        paid_users: 0,
        checkout_count: 0,
        topup_count: 0,
      };
      byDate.set(r.metric_date, entry);
    }
    assignMetric(entry, r.metric_name, r.metric_value, REVENUE_FIELDS);
  }

  const rows = sortByDate(byDate);
  return {
    rows,
    totals: {
      mrr: last(rows, "mrr"),
      paid_users: sum(rows, "paid_users"),
      checkout_count: sum(rows, "checkout_count"),
      topup_count: sum(rows, "topup_count"),
    },
  };
}

function toSystemMetrics(items: ReadonlyArray<MetricRow>): SystemMetrics {
  const byDate = new Map<string, Mutable<SystemDayRow>>();

  for (const r of items) {
    let entry = byDate.get(r.metric_date);
    if (!entry) {
      entry = {
        date: r.metric_date,
        summary_requests: 0,
        summary_success: 0,
        summary_failed: 0,
        storage_bytes: 0,
        webhook_failures: 0,
      };
      byDate.set(r.metric_date, entry);
    }
    assignMetric(entry, r.metric_name, r.metric_value, SYSTEM_FIELDS);
  }

  const rows = sortByDate(byDate);
  const reqs = sum(rows, "summary_requests");
  const succ = sum(rows, "summary_success");
  return {
    rows,
    totals: {
      summary_requests: reqs,
      success_rate: reqs > 0 ? succ / reqs : 0,
      storage_bytes: last(rows, "storage_bytes"),
      webhook_failures: sum(rows, "webhook_failures"),
    },
  };
}

function toBehaviorMetrics(raw: BehaviorResponse): BehaviorMetrics {
  const byDate = new Map<string, Mutable<BehaviorDayRow>>();

  for (const r of raw.behavior) {
    let entry = byDate.get(r.metric_date);
    if (!entry) {
      entry = {
        date: r.metric_date,
        sessions_per_user: 0,
        messages_per_user: 0,
        summaries_per_user: 0,
        bookmarks_per_user: 0,
        shares_per_user: 0,
        agent_usage_rate: 0,
        retention_rate: 0,
      };
      byDate.set(r.metric_date, entry);
    }
    assignMetric(entry, r.metric_name, r.metric_value, BEHAVIOR_FIELDS);
  }

  const msgTotals = { human: 0, agent: 0, group: 0 };
  for (const r of raw.messages) {
    if (r.metric_name in msgTotals) {
      msgTotals[r.metric_name as keyof typeof msgTotals] += r.metric_value;
    }
  }

  const rows = sortByDate(byDate);
  return {
    rows,
    totals: {
      sessions_per_user: avg(rows, "sessions_per_user"),
      messages_per_user: avg(rows, "messages_per_user"),
      summaries_per_user: avg(rows, "summaries_per_user"),
      bookmarks_per_user: avg(rows, "bookmarks_per_user"),
      shares_per_user: avg(rows, "shares_per_user"),
      agent_usage_rate: avg(rows, "agent_usage_rate"),
      retention_rate: avg(rows, "retention_rate"),
    },
    message_breakdown: msgTotals,
  };
}

function toFeatureValueMetrics(raw: FeatureValueResponse): FeatureValueMetrics {
  const grouped = new Map<string, Mutable<FeatureValueRow>>();

  for (const r of raw.items) {
    const existing = grouped.get(r.feature_name);
    if (existing) {
      existing.exposure += r.exposure_count;
      existing.adoption += r.adoption_count;
      existing.repeat += r.repeat_count;
    } else {
      grouped.set(r.feature_name, {
        feature_name: r.feature_name,
        exposure: r.exposure_count,
        adoption: r.adoption_count,
        repeat: r.repeat_count,
        retention_lift: r.retention_lift ?? 0,
      });
    }
  }

  return { rows: Array.from(grouped.values()) };
}

// ─── Metric field mappings ───

const GROWTH_FIELDS: Record<string, string> = {
  dau: "dau",
  new_users: "new_users",
  messages: "messages",
  summary_requests: "summary_requests",
};

const REVENUE_FIELDS: Record<string, string> = {
  mrr: "mrr",
  paid_users: "paid_users",
  checkout_completed: "checkout_count",
  topup_completed: "topup_count",
};

const SYSTEM_FIELDS: Record<string, string> = {
  summary_requested: "summary_requests",
  summary_completed: "summary_success",
  summary_failed: "summary_failed",
  storage_growth: "storage_bytes",
  webhook_failures: "webhook_failures",
};

const BEHAVIOR_FIELDS: Record<string, string> = {
  sessions_per_user: "sessions_per_user",
  messages_per_user: "messages_per_user",
  summaries_per_user: "summaries_per_user",
  bookmarks_per_user: "bookmarks_per_user",
  shares_per_user: "shares_per_user",
  agent_usage_rate: "agent_usage_rate",
  retention_rate: "retention_rate",
};

// ─── Helpers ───

type Mutable<T> = { -readonly [K in keyof T]: T[K] };

function assignMetric(
  target: Record<string, unknown>,
  metricName: string,
  value: number,
  mapping: Record<string, string>,
): void {
  const field = mapping[metricName];
  if (field) {
    target[field] = value;
  }
}

function sortByDate<T extends { date: string }>(map: Map<string, T>): T[] {
  return Array.from(map.values()).sort((a, b) => a.date.localeCompare(b.date));
}

function sum<T>(rows: ReadonlyArray<T>, k: keyof T): number {
  let t = 0;
  for (const r of rows) t += Number(r[k]);
  return t;
}

function avg<T>(rows: ReadonlyArray<T>, k: keyof T): number {
  return sum(rows, k) / (rows.length || 1);
}

function last<T>(rows: ReadonlyArray<T>, k: keyof T): number {
  return rows.length > 0 ? Number(rows[rows.length - 1][k]) : 0;
}

function toQueryString(obj: Record<string, string>): string {
  return new URLSearchParams(obj).toString();
}
